import React, { useState, useEffect, KeyboardEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Header } from "../components/Header";
import { RedditPost, RedditPostProps } from "../components/RedditPost";
import { CanvasView } from "../components/CanvasView";
import { MagicLoader } from "../components/MagicLoader";
import { /* Bell, */ LogIn, LogOut, User, Grid } from "lucide-react";
import { motion } from "framer-motion";
import axios from "axios";
import Confetti from "react-confetti";
import { API_ENDPOINTS } from '../config/api';
import { getRedditAuthUrl, exchangeCodeForToken, validateToken, REDDIT_CONFIG, handleImplicitAuthResponse } from '../config/reddit';
import { logger } from '../utils/logger';
import { toast, ToastContainer } from 'react-toastify';
import { initializeUserTracking, trackPostView, trackKeywordSearch, updateUserAfterLogin } from '../utils/userTracking';
import 'react-toastify/dist/ReactToastify.css';
import { trackEvent, setUserProperties } from '../config/amplitude';

interface Post extends RedditPostProps {}

interface CommentData {
  postId: string;
  commentId: string;
  subreddit: string;
  commentText: string;
  createdAt?: number;
}

export const FeedPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Move these up before other state declarations
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loadingPreferences, setLoadingPreferences] = useState(true);
  const [statusMessage, setStatusMessage] = useState<{type: 'success' | 'error' | 'info', text: string} | null>(null);

  // Add useEffect for auto-dismissing status messages
  useEffect(() => {
    if (statusMessage) {
      const timer = setTimeout(() => {
        setStatusMessage(null);
      }, 5000); // 5 seconds

      return () => clearTimeout(timer);
    }
  }, [statusMessage]);

  const initialTags = location.state?.keywords || [];
  const suggestedTags = ["promote your app", "best ai tools", "best saas tools"];
  const [modalTags, setModalTags] = useState<string[]>([]);
  
  const [tags, setTags] = useState<string[]>([]);
  const [activeTags, setActiveTags] = useState<string[]>([]);
  const [posts, setPosts] = useState<Post[]>([]);
  const [newTag, setNewTag] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [savingPreferences, setSavingPreferences] = useState<boolean>(false);
  const [isCanvasViewActive, setIsCanvasViewActive] = useState(false);
  const [sortBy, setSortBy] = useState<'date' | 'comments'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Add retry count state
  const [loginRetryCount, setLoginRetryCount] = useState(0);
  const MAX_RETRIES = 3;
  const RETRY_DELAY = 2000;

  // Helper function to get the start of day timestamp
  const getStartOfDay = (timestamp: number) => {
    const date = new Date(timestamp * 1000);
    date.setHours(0, 0, 0, 0);
    return date.getTime();
  };

  // Helper function to format the date
  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp * 1000);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    // Check if date is today
    if (date.toDateString() === today.toDateString()) {
      return "Today";
    }
    // Check if date is yesterday
    if (date.toDateString() === yesterday.toDateString()) {
      return "Yesterday";
    }
    // Otherwise return the date
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const sourceUrl: string | undefined = location.state?.sourceUrl;

  // Modal state
  const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
  const [email, setEmail] = useState<string>("");
  const [showConfetti, setShowConfetti] = useState(false);
  const [commentedPosts, setCommentedPosts] = useState<Set<string>>(new Set());

  // For Undo delete feature
  const [previousTags, setPreviousTags] = useState<string[]>([]);
  const [showUndo, setShowUndo] = useState(false);

  // Add payment screen state
  // const [showPaymentScreen, setShowPaymentScreen] = useState(false);

  // Add initialization effect
  useEffect(() => {
    const token = localStorage.getItem('reddit_token');
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
  }, []);

  // Helper function to handle rate limit
  const handleRateLimit = async (error: any) => {
    if (error.response?.status === 429) {
      const retryAfter = error.response.headers['retry-after'] || 2;
      const message = `Rate limited. Please try again in ${retryAfter} seconds.`;
      setStatusMessage({ type: 'error', text: message });
      return new Promise(resolve => setTimeout(resolve, retryAfter * 1000));
    }
    throw error;
  };

  // Handle login click
  const handleLogin = () => {
    try {
      // Save current tags to localStorage before redirecting
      if (activeTags.length > 0 || tags.length > 0) {
        localStorage.setItem('temp_active_tags', JSON.stringify(activeTags));
        localStorage.setItem('temp_all_tags', JSON.stringify(tags));
      }
      const authUrl = getRedditAuthUrl();
      window.location.href = authUrl;
    } catch (error) {
      setStatusMessage({
        type: 'error',
        text: 'Failed to generate login URL'
      });
    }
  };

  // Check if user is authenticated and handle redirect
  useEffect(() => {
    let isEffectActive = true; // For cleanup

    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('reddit_token');
        
        // First check for implicit flow response
        const { token: implicitToken, error: implicitError } = handleImplicitAuthResponse();
        
        if (implicitToken) {
          localStorage.setItem('reddit_token', implicitToken);
          setIsAuthenticated(true);
          setStatusMessage({ type: 'success', text: 'Successfully logged in!' });
          // Clean up URL
          window.history.replaceState({}, document.title, window.location.pathname);
          return;
        }
        
        if (implicitError) {
          setStatusMessage({
            type: 'error',
            text: `Login failed: ${implicitError}`
          });
          handleLogout();
          return;
        }

        // If we have a token, validate it
        if (token) {
          setIsAuthenticated(true);
          return;
        }

        setLoadingPreferences(false);
        
      } catch (error) {
        if (isEffectActive) {
          setLoadingPreferences(false);
          setStatusMessage({
            type: 'error',
            text: error instanceof Error ? error.message : 'Authentication failed'
          });
        }
      }
    };

    // Execute auth check
    checkAuth().catch(error => {
      if (isEffectActive) {
        setLoadingPreferences(false);
      }
    });

    // Cleanup function
    return () => {
      isEffectActive = false;
    };
  }, []); // Empty dependency array means this runs once on mount

  // Load saved keywords from user preferences
  useEffect(() => {
    const loadKeywords = async () => {
      if (!isAuthenticated) {
        if (initialTags.length > 0) {
          setTags([...initialTags, ...suggestedTags]);
          setActiveTags(initialTags);
        } else {
          setTags(suggestedTags);
          setActiveTags([]);
        }
        setLoadingPreferences(false);
        return;
      }

      try {
        const token = localStorage.getItem('reddit_token');
        if (!token) {
          setLoadingPreferences(false);
          return;
        }

        const response = await axios.get(API_ENDPOINTS.USER_PREFERENCES, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const savedKeywords = response.data?.savedKeywords || [];
        
        // Check for temporary tags from pre-login state
        const tempActiveTags = localStorage.getItem('temp_active_tags');
        const tempAllTags = localStorage.getItem('temp_all_tags');
        
        if (tempActiveTags && tempAllTags && savedKeywords.length === 0) {
          // First time login - use the temporary tags
          const parsedActiveTags = JSON.parse(tempActiveTags);
          const parsedAllTags = JSON.parse(tempAllTags);
          setTags([...parsedAllTags, ...suggestedTags]);
          setActiveTags(parsedActiveTags);
          
          // Save these tags to user preferences
          await axios.post(
            API_ENDPOINTS.USER_PREFERENCES,
            { savedKeywords: parsedActiveTags },
            {
              headers: { Authorization: `Bearer ${token}` }
            }
          );
          
          // Clear temporary storage
          localStorage.removeItem('temp_active_tags');
          localStorage.removeItem('temp_all_tags');
          
          setStatusMessage({type: 'success', text: 'Welcome! Your keywords have been saved.'});
        } else if (initialTags.length > 0) {
          setTags([...initialTags, ...suggestedTags]);
          setActiveTags(initialTags);
        } else if (savedKeywords.length > 0) {
          setTags([...savedKeywords, ...suggestedTags]);
          setActiveTags(savedKeywords);
          setStatusMessage({type: 'success', text: 'Welcome back!'});
        } else {
          setTags(suggestedTags);
          setActiveTags([]);
        }
      } catch (err: any) {
        if (axios.isAxiosError(err) && err.response?.status === 401) {
          handleLogout();
          return;
        }
        setTags(suggestedTags);
        setActiveTags([]);
      } finally {
        setLoadingPreferences(false);
      }
    };

    loadKeywords();
  }, [isAuthenticated]); // Only run when authentication state changes

  // Load commented posts
  useEffect(() => {
    const loadCommentedPosts = async () => {
      if (!isAuthenticated) {
        return;
      }
      
      try {
        const token = localStorage.getItem('reddit_token');
        
        const response = await axios.get(API_ENDPOINTS.USER_COMMENTS, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Create a Set of post IDs that have been commented on
        const commentsList = response.data.comments || [];
        const commentedPostIds = new Set(commentsList.map((comment: CommentData) => comment.postId));
        
        setCommentedPosts(commentedPostIds as Set<string>);
      } catch (error) {
        // Silently handle error
      }
    };

    loadCommentedPosts();
  }, [isAuthenticated]);

  // Fetch posts when active tags change
  useEffect(() => {
    const fetchPosts = async () => {
      if (loading) return; // Prevent multiple simultaneous fetches
      
      setLoading(true);
      
      try {
        if (activeTags.length === 0) {
          setPosts([]);
          return;
        }
        
        const response = await axios.post<{ posts: Post[] }>(
          API_ENDPOINTS.FETCH_POSTS,
          { keywords: activeTags }
        );
        
        setPosts(response.data.posts);
      } catch (err) {
        setError("Failed to fetch posts");
        setPosts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, [activeTags]); // Only re-fetch when activeTags changes

  const toggleTag = (tag: string) => {
    setActiveTags((prev) => {
      const newActiveTags = prev.includes(tag) 
        ? prev.filter((t) => t !== tag) 
        : [...prev, tag];
      trackEvent('Tag Toggled', { 
        tag, 
        action: prev.includes(tag) ? 'removed' : 'added' 
      });
      return newActiveTags;
    });
  };

  const removeTag = (tag: string) => {
    setTags((prev) => prev.filter((t) => t !== tag));
    setActiveTags((prev) => prev.filter((t) => t !== tag));
    trackEvent('Tag Removed', { tag });
  };

  const handleAddTag = () => {
    const trimmedTag = newTag.trim();
    if (trimmedTag && !tags.includes(trimmedTag)) {
      const newTags = [...tags, trimmedTag];
      setTags(newTags);
      setActiveTags([...activeTags, trimmedTag]);
      setNewTag("");
      if (isAuthenticated) {
        savePreferences(activeTags);
      }
      trackEvent('Tag Added', { tag: trimmedTag });
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") handleAddTag();
  };

  const handleSelectAll = () => {
    setActiveTags([...tags]);
  };

  const handleDeleteAll = () => {
    setPreviousTags(tags);
    setTags([]);
    setActiveTags([]);
    setShowUndo(true);
  };

  const handleUndoDelete = () => {
    setTags(previousTags);
    setActiveTags(previousTags);
    setShowUndo(false);
  };

  const openAlertModal = () => {
    setModalTags(activeTags); // Create a separate copy of activeTags for the modal
    setIsAlertModalOpen(true);
  };

  const closeAlertModal = () => {
    setIsAlertModalOpen(false);
  };

  const handleCreateAlert = () => {
    setShowConfetti(true);
    setTimeout(() => {
      setShowConfetti(false);
      closeAlertModal();
    }, 3000); // Confetti for 3 seconds
  };

  const handleSubmit = async () => {
    try {
      const response = await axios.post(API_ENDPOINTS.SUBMIT_POST, {
        // ... post data
      });
      // ... rest of the function
    } catch (error) {
      logger.error('Error submitting post:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('reddit_token');
    localStorage.removeItem('reddit_user');
    setIsAuthenticated(false);
    setTags(suggestedTags);
    setActiveTags([]);
    setStatusMessage({type: 'info', text: 'You have been logged out'});
    trackEvent('Logged Out');
  };

  // Add daily comment count state
  const [dailyCommentCount, setDailyCommentCount] = useState<number>(0);
  const DAILY_COMMENT_LIMIT = 5;

  // Add function to check if user has reached daily limit
  const checkDailyCommentLimit = async (): Promise<boolean> => {
    try {
      const token = localStorage.getItem('reddit_token');
      if (!token) return false;

      const response = await axios.get(API_ENDPOINTS.USER_COMMENTS, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const todaysComments = (response.data.comments || []).filter((comment: CommentData) => {
        if (!comment.createdAt) return false;
        const commentDate = new Date(comment.createdAt);
        commentDate.setHours(0, 0, 0, 0);
        return commentDate.getTime() === today.getTime();
      });

      setDailyCommentCount(todaysComments.length);
      return todaysComments.length >= DAILY_COMMENT_LIMIT;
    } catch (error) {
      console.error('Error checking daily comment limit:', error);
      return false;
    }
  };

  // Update trackComment function to include limit check
  const trackComment = async (postId: string, commentId: string, subreddit: string, commentText: string) => {
    try {
      const hasReachedLimit = await checkDailyCommentLimit();
      if (hasReachedLimit) {
        toast.warning(`You've reached the daily limit of ${DAILY_COMMENT_LIMIT} comments. Please try again tomorrow.`, {
          position: "top-center",
          autoClose: 5000
        });
        trackEvent('Comment Limit Reached', {
          dailyLimit: DAILY_COMMENT_LIMIT,
          commentCount: dailyCommentCount
        });
        return false;
      }

      await axios.post(
        API_ENDPOINTS.TRACK_COMMENT,
        {
          postId,
          commentId,
          subreddit,
          commentText,
          createdAt: Date.now()
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('reddit_token')}`
          }
        }
      );

      setDailyCommentCount(prev => prev + 1);
      setCommentedPosts(prev => new Set(Array.from(prev).concat(postId)));
      
      trackEvent('Comment Posted', { 
        postId, 
        subreddit,
        commentLength: commentText.length,
        dailyCommentCount: dailyCommentCount + 1
      });

      return true;
    } catch (error) {
      console.error("Error tracking comment:", error);
      return false;
    }
  };

  // Load initial daily comment count
  useEffect(() => {
    if (isAuthenticated) {
      checkDailyCommentLimit();
    }
  }, [isAuthenticated]);

  // Save preferences function
  const savePreferences = async (keywords: string[]) => {
    if (!isAuthenticated) return;
    
    setSavingPreferences(true);
    setStatusMessage({type: 'info', text: 'Saving keywords...'});
    
    try {
      await axios.post(
        API_ENDPOINTS.USER_PREFERENCES,
        { savedKeywords: keywords },
        { headers: { Authorization: `Bearer ${localStorage.getItem('reddit_token')}` } }
      );
      setStatusMessage({type: 'success', text: 'Keywords saved successfully!'});
    } catch (error) {
      logger.error('Error saving preferences:', error);
      setStatusMessage({type: 'error', text: 'Failed to save keywords'});
    } finally {
      setSavingPreferences(false);
    }
  };

  // Add effect for posts logging
  useEffect(() => {
    logger.info('=== RENDERING POSTS ===', { postsCount: posts.length, commentedPostsCount: commentedPosts.size });
  }, [posts.length, commentedPosts.size]);

  const openCanvasView = () => {
    setIsCanvasViewActive(true);
    trackEvent('Canvas View Opened', { 
      activeTagsCount: activeTags.length,
      postsCount: posts.length 
    });
  };

  // Initialize user tracking on mount
  useEffect(() => {
    const initTracking = async () => {
      await initializeUserTracking(!isAuthenticated);
    };
    initTracking();
  }, [isAuthenticated]);

  // Update user tracking after login
  useEffect(() => {
    const updateTracking = async () => {
      if (isAuthenticated) {
        await updateUserAfterLogin();
      }
    };
    updateTracking();
  }, [isAuthenticated]);

  // Track post views when posts change
  useEffect(() => {
    if (posts.length > 0) {
      trackPostView();
    }
  }, [posts]);

  // Track keyword searches
  useEffect(() => {
    if (activeTags.length > 0) {
      activeTags.forEach(tag => trackKeywordSearch(tag));
    }
  }, [activeTags]);

  // Track when user logs in
  useEffect(() => {
    if (isAuthenticated) {
      setUserProperties({
        isAuthenticated: true,
        hasRedditToken: !!localStorage.getItem('reddit_token')
      });
      trackEvent('Logged In');
    }
  }, [isAuthenticated]);

  if (loading) return <MagicLoader />;
  if (error) return <div className="text-center text-red-600">{error}</div>;

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-gradientStart to-gradientEnd">
      <Header 
        isAuthenticated={isAuthenticated} 
        onLogin={handleLogin} 
        onLogout={handleLogout}
      />

      <ToastContainer position="top-right" />
      
      {/* Status Message */}
      {statusMessage && (
        <div className={`fixed top-20 left-1/2 transform -translate-x-1/2 px-4 py-2 rounded-md shadow-md z-50 ${
          statusMessage.type === 'success' ? 'bg-green-100 text-green-700' :
          statusMessage.type === 'error' ? 'bg-red-100 text-red-700' :
          'bg-blue-100 text-blue-700'
        } transition-opacity duration-300`}>
          {statusMessage.text}
        </div>
      )}

      {/* Loading Indicator */}
      {(savingPreferences) && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 bg-white px-4 py-2 rounded-full shadow-md z-50">
          <div className="flex items-center space-x-2">
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-orange-500 border-t-transparent"></div>
            <span className="text-sm text-gray-600">
              Saving keywords...
            </span>
          </div>
        </div>
      )}

      <div className="max-w-6xl mx-auto py-8 px-4 mt-16 relative">
        {/* Source URL */}
        {sourceUrl && (
          <div className="p-4 bg-white rounded-md shadow mb-6">
            <p className="text-sm text-gray-500 mb-1">Source URL:</p>
            <a
              href={sourceUrl}
              className="text-orange-500 hover:underline break-all"
              target="_blank"
              rel="noreferrer"
            >
              {sourceUrl}
            </a>
          </div>
        )}

        {/* Tags Section */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="flex-1 flex flex-wrap gap-3">
            {tags.map((tag) => (
              <div key={tag} className="relative flex items-center">
                <span
                  onClick={() => toggleTag(tag)}
                  className={`cursor-pointer px-3 py-1 rounded-full text-sm font-medium transition-all ${
                    activeTags.includes(tag)
                      ? "bg-sky-500 text-white"
                      : "bg-white text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  {tag}
                </span>
                <button
                  onClick={() => removeTag(tag)}
                  className="ml-1 text-xs text-gray-500 hover:text-gray-800"
                >
                  ✕
                </button>
              </div>
            ))}

            <input
              type="text"
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Add keyword"
              className="border border-gray-300 px-3 py-1 rounded-md text-sm"
            />

            <button
              onClick={handleAddTag}
              className="px-3 py-1 rounded-full bg-sky-100 text-sky-600 text-sm font-medium hover:bg-sky-200"
            >
              Add
            </button>

            <button
              onClick={handleSelectAll}
              className="px-3 py-1 rounded-full bg-sky-100 text-sky-600 text-sm font-medium hover:bg-sky-200"
            >
              Select All
            </button>

            <button
              onClick={handleDeleteAll}
              className="px-3 py-1 rounded-full bg-red-100 text-red-600 text-sm font-medium hover:bg-red-200"
            >
              Delete All
            </button>

            {showUndo && (
              <button
                onClick={handleUndoDelete}
                className="px-3 py-1 rounded-full bg-blue-100 text-blue-600 text-sm font-medium hover:bg-blue-200 animate-fade-in"
              >
                Undo Delete
              </button>
            )}

            {isAuthenticated && activeTags.length > 0 && (
              <button
                onClick={() => savePreferences(activeTags)}
                disabled={savingPreferences}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                  savingPreferences 
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-green-100 text-green-600 hover:bg-green-200'
                }`}
                title="Save active keywords to your preferences"
              >
                {savingPreferences ? 'Saving...' : 'Save Active Keywords'}
              </button>
            )}
          </div>
        </div>

        {/* Posts Section */}
        {posts.length > 0 && (
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-4 bg-white rounded-lg p-2">
                <span className="text-gray-600">Sort by:</span>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setSortBy('date')}
                    className={`px-4 py-2 rounded-lg transition-colors ${
                      sortBy === 'date'
                        ? 'bg-sky-500 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {sortBy === 'date' && sortOrder === 'desc' ? 'Newest First' : 
                     sortBy === 'date' && sortOrder === 'asc' ? 'Oldest First' : 'Date'}
                  </button>
                  <button
                    onClick={() => setSortBy('comments')}
                    className={`px-4 py-2 rounded-lg transition-colors ${
                      sortBy === 'comments'
                        ? 'bg-sky-500 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {sortBy === 'comments' && sortOrder === 'desc' ? 'Most Comments' : 
                     sortBy === 'comments' && sortOrder === 'asc' ? 'Least Comments' : 'Comments'}
                  </button>
                  <button
                    onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                    className="px-4 py-2 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200"
                  >
                    {sortOrder === 'desc' ? '↓ Descending' : '↑ Ascending'}
                  </button>
                </div>
              </div>
            </div>

            {/* Canvas View Button */}
            <div className="relative group">
              <span className="absolute -top-1 -right-1 text-yellow-400">✨</span>
              <button
                onClick={openCanvasView}
                className="flex items-center space-x-2 bg-sky-500 text-white px-4 py-2 rounded-lg hover:bg-sky-600 transition-colors whitespace-nowrap"
              >
                <Grid size={20} />
                <span>Canvas View</span>
              </button>
              <div className="absolute right-0 -bottom-8 bg-gray-800 text-white text-xs rounded py-1 px-2 invisible group-hover:visible transition-opacity whitespace-nowrap">
                See all posts at once
              </div>
            </div>
          </div>
        )}

        {/* Posts */}
        {posts.length > 0 ? (
          posts
            .sort((a, b) => {
              if (sortBy === 'date') {
                const dateCompare = (b.created_utc || 0) - (a.created_utc || 0);
                return sortOrder === 'desc' ? dateCompare : -dateCompare;
              } else { // sortBy === 'comments'
                const commentCompare = (b.num_comments || 0) - (a.num_comments || 0);
                return sortOrder === 'desc' ? commentCompare : -commentCompare;
              }
            })
            .map((post) => {
              const hasCommented = commentedPosts.has(post.id);
              return (
                <RedditPost
                  key={post.id}
                  id={post.id}
                  title={post.title}
                  selftext={post.selftext}
                  subreddit={post.subreddit}
                  url={post.url}
                  ups={post.ups}
                  num_comments={post.num_comments}
                  created_utc={post.created_utc}
                  keywords={activeTags}
                  hasCommented={hasCommented}
                  onCommentPosted={trackComment}
                  author={post.author}
                  score={post.score}
                  permalink={post.permalink}
                  isAuthenticated={isAuthenticated}
                />
              );
            })
        ) : (
          <p className="text-center text-gray-600">No posts found.</p>
        )}
      </div>

      {/* Canvas View */}
      {isCanvasViewActive && (
        <CanvasView
          posts={posts}
          onClose={() => setIsCanvasViewActive(false)}
          activeTags={activeTags}
          commentedPosts={commentedPosts}
          onCommentPosted={trackComment}
          isAuthenticated={isAuthenticated}
        />
      )}

      {/* Alert Modal */}
      {isAlertModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="bg-white p-6 rounded-lg shadow-lg w-[90%] max-w-md"
          >
            <h2 className="text-lg font-semibold mb-4">Create Alert</h2>
            <p className="text-gray-600 mb-4">Alerts will be created for the following keywords:</p>

            {/* Editable Tags */}
            <div className="flex flex-wrap gap-2 mb-4">
              {modalTags.map((tag) => (
                <div
                  key={tag}
                  className="flex items-center bg-orange-100 text-orange-700 px-2 py-1 rounded-full text-sm"
                >
                  <span>{tag}</span>
                  <button
                    onClick={() => setModalTags((prev) => prev.filter((t) => t !== tag))}
                    className="ml-2 text-orange-500 hover:text-orange-700"
                    title="Remove tag"
                    type="button"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>

            {/* Email Input */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Your Email</label>
              <input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-orange-500 focus:outline-none"
                required
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={closeAlertModal}
                className="text-gray-600 hover:text-gray-800"
                type="button"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateAlert}
                className="bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600"
                type="button"
              >
                Create Alert
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {showConfetti && (
        <div className="fixed inset-0 pointer-events-none">
          <Confetti
            width={window.innerWidth}
            height={window.innerHeight}
            numberOfPieces={300}
            gravity={0.3}
          />
        </div>
      )}
    </div>
  );
};
